import React, { memo } from 'react';

import { Box } from '@mui/material';
import EditExtractionField from 'app/shared/components/document/ExtractionField/EditExtractionFields/EditExtractionField';
import PropTypes from 'prop-types';

import { DOC_FIELD_TYPES, dropDownDocumentFieldTypes } from 'constants/documentFieldTypes';

import { WORKSHEET_FIELD_FORMAT } from '../../WorksheetFieldsLayout.constants';
import { getWorksheetFieldHint } from '../../WorksheetFieldsLayout.utils';
import WorksheetDateField from '../WorksheetDateField';
import WorksheetTextField from '../WorksheetTextField';
import WorksheetYesNoField from '../WorksheetYesNoField';

import { structuredFieldStyles } from './WorksheetField.styles';

const SCALAR_TEXT_TYPES = [DOC_FIELD_TYPES.TEXT, DOC_FIELD_TYPES.INTEGER, DOC_FIELD_TYPES.DOUBLE];

/**
 * One worksheet question: Yes/No, date and text answers use the worksheet controls; dropdowns and
 * anything structured keep the extraction form's own field — a table with its add/remove row
 * buttons (LEN-194: repeating rows).
 */
const WorksheetField = ({
  field,
  settings,
  form,
  errors,
  disabled = false,
  extraHint = null,
  setInForm,
  validateField,
  onChange,
  onAdd = null,
  onRemove = null,
}) => {
  const { id, name, type, isRequired, nestedFields, nestedGroups } = field;
  const value = form?.[id];
  const error = errors?.[id] ?? null;

  if (type === DOC_FIELD_TYPES.BOOL) {
    return (
      <WorksheetYesNoField
        disabled={disabled}
        error={error}
        id={id}
        isRequired={isRequired}
        name={name}
        setInForm={setInForm}
        validateField={validateField}
        value={value ?? null}
      />
    );
  }

  if (type === DOC_FIELD_TYPES.DATE) {
    return (
      <WorksheetDateField
        disabled={disabled}
        error={error}
        id={id}
        isRequired={isRequired}
        name={name}
        setInForm={setInForm}
        validateField={validateField}
        value={value ?? null}
      />
    );
  }

  if (SCALAR_TEXT_TYPES.includes(type) && !dropDownDocumentFieldTypes.includes(type)) {
    const format = Object.values(WORKSHEET_FIELD_FORMAT).includes(settings.format)
      ? settings.format
      : null;

    return (
      <WorksheetTextField
        disabled={disabled}
        error={error}
        format={format}
        hint={getWorksheetFieldHint({ settings, extraHint })}
        id={id}
        isRequired={isRequired}
        name={name}
        setInForm={setInForm}
        validateField={validateField}
        value={value ?? ''}
      />
    );
  }

  return (
    <Box sx={structuredFieldStyles}>
      <EditExtractionField
        disabled={disabled}
        errors={errors}
        form={form}
        id={id}
        isRequired={isRequired}
        name={name}
        nestedFields={nestedFields}
        nestedGroups={nestedGroups}
        setInForm={setInForm}
        type={type}
        validateField={validateField}
        onAdd={onAdd}
        onChange={onChange}
        onRemove={onRemove}
      />
    </Box>
  );
};

WorksheetField.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    nestedFields: PropTypes.arrayOf(PropTypes.object),
    nestedGroups: PropTypes.object,
  }).isRequired,
  settings: PropTypes.shape({
    format: PropTypes.string,
    hint: PropTypes.string,
  }).isRequired,
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  extraHint: PropTypes.string,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
};

export default memo(WorksheetField);
